import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { supabase } from "../lib/supabase";
import type { Item, Withdrawal } from "../lib/types";
import { LoadingBlock, Notice, PageHeading, StockBadge } from "../components/Ui";
import { InventoryImportPanel } from "../components/InventoryImportPanel";

type ItemForm = {
  brand: string;
  model: string;
  stockArea: string;
  location: string;
  alertThreshold: number;
  quantity: number;
  quantityVerified: boolean;
  active: boolean;
};

type ExportItem = Pick<Item, "id" | "brand" | "model" | "stock_area" | "location">;
type ExportWithdrawal = Withdrawal & { items?: ExportItem };
type ExportReturn = {
  id: string;
  withdrawal_id: string;
  person_name: string;
  quantity: number;
  notes: string | null;
  created_at: string;
};
type ExportAdjustment = {
  id: string;
  item_id: number;
  person_name: string;
  quantity_before: number;
  quantity_delta: number;
  quantity_after: number;
  reason: string;
  created_at: string;
  items?: ExportItem;
};
type ExportMovement = {
  createdAt: Date;
  movement: string;
  itemId: number;
  brand: string;
  model: string;
  stockArea: string;
  location: string;
  personName: string;
  personEmail: string;
  quantity: number;
  usage: string;
  destination: string;
  expectedReturn: Date | null;
  status: string;
};

type CreatedQr = {
  item: Item;
  dataUrl: string;
};

async function loadAllExportRows(table: "withdrawals" | "returns" | "stock_adjustments", selection: string) {
  const pageSize = 1000;
  const rows: Record<string, unknown>[] = [];
  for (let start = 0; ; start += pageSize) {
    const { data, error } = await supabase
      .from(table)
      .select(selection)
      .order("created_at", { ascending: false })
      .range(start, start + pageSize - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as Record<string, unknown>[];
    rows.push(...page);
    if (page.length < pageSize) break;
  }
  return rows;
}

const emptyForm: ItemForm = {
  brand: "",
  model: "",
  stockArea: "ASP 0E",
  location: "",
  alertThreshold: 4,
  quantity: 0,
  quantityVerified: true,
  active: true,
};

export function InventoryAdminPage() {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedId, setSelectedId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<ItemForm>(emptyForm);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [createdQr, setCreatedQr] = useState<CreatedQr | null>(null);
  const [adjustment, setAdjustment] = useState({ quantity: 1, direction: "add" as "add" | "remove", reason: "Réception de matériel" });
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  const load = async (keepSelection = true) => {
    const { data, error } = await supabase.from("items").select("*").order("brand").order("model");
    if (error) setMessage({ tone: "error", text: error.message });
    const nextItems = (data ?? []) as Item[];
    setItems(nextItems); setLoading(false);
    if (keepSelection && typeof selectedId === "number") {
      const refreshed = nextItems.find((item) => item.id === selectedId);
      if (refreshed) selectItem(refreshed);
    }
  };
  useEffect(() => { load(false); }, []);

  const selectItem = (item: Item) => {
    setSelectedId(item.id);
    setForm({
      brand: item.brand,
      model: item.model,
      stockArea: item.stock_area,
      location: item.location,
      alertThreshold: item.alert_threshold,
      quantity: item.current_quantity,
      quantityVerified: item.quantity_verified,
      active: item.active,
    });
    setMessage(null);
  };

  const startNew = () => { setSelectedId("new"); setForm(emptyForm); setCreatedQr(null); setMessage(null); };
  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("fr");
    return items.filter((item) => !needle || `${item.brand} ${item.model} ${item.stock_area} ${item.location}`.toLocaleLowerCase("fr").includes(needle));
  }, [items, search]);
  const selected = typeof selectedId === "number" ? items.find((item) => item.id === selectedId) ?? null : null;

  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setMessage(null);
    const quantity = Number(form.quantity);
    const alertThreshold = Number(form.alertThreshold);
    const metadata = {
      brand: form.brand.trim(), model: form.model.trim(), stock_area: form.stockArea.trim(), location: form.location.trim(),
      alert_threshold: alertThreshold, quantity_verified: form.quantityVerified, active: form.active,
    };
    if (!metadata.brand || !metadata.model || !metadata.stock_area || !metadata.location) {
      setBusy(false); return setMessage({ tone: "error", text: "Marque, modèle, stock et emplacement sont obligatoires." });
    }
    if (!Number.isInteger(quantity) || quantity < 0 || !Number.isInteger(alertThreshold) || alertThreshold < 0) {
      setBusy(false); return setMessage({ tone: "error", text: "La quantité et le seuil d’alerte doivent être des nombres entiers positifs." });
    }
    if (selectedId === "new") {
      const { data, error } = await supabase.from("items").insert({ ...metadata, initial_quantity: quantity, current_quantity: quantity }).select("*").single();
      if (error || !data) { setBusy(false); return setMessage({ tone: "error", text: error?.message ?? "Création impossible." }); }
      const createdItem = data as Item;
      const dataUrl = await QRCode.toDataURL(`${window.location.origin}/item/${createdItem.id}`, {
        margin: 1,
        color: { dark: "#0b1f33", light: "#ffffff" },
        width: 420,
      });
      setCreatedQr({ item: createdItem, dataUrl });
      setMessage({ tone: "success", text: "Le nouvel article a été ajouté. Son QR code est prêt à télécharger ou à imprimer." });
      setSelectedId(null); setForm(emptyForm); await load(false);
    } else if (selected) {
      const { error } = await supabase.from("items").update(metadata).eq("id", selected.id);
      if (error) { setBusy(false); return setMessage({ tone: "error", text: error.message }); }
      const desiredStock = quantity;
      const delta = desiredStock - selected.current_quantity;
      if (delta !== 0) {
        const { error: stockError } = await supabase.rpc("admin_adjust_stock", {
          p_item_id: selected.id,
          p_quantity_delta: delta,
          p_reason: "Correction du stock physique depuis la fiche administrateur",
        });
        if (stockError) { setBusy(false); return setMessage({ tone: "error", text: stockError.message }); }
      }
      setMessage({ tone: "success", text: `L’article a été mis à jour. L’alerte e-mail se déclenchera à ${alertThreshold} pièce(s) restantes.` }); await load(true);
    }
    setBusy(false);
  };

  const downloadCreatedQr = () => {
    if (!createdQr) return;
    const anchor = document.createElement("a");
    anchor.href = createdQr.dataUrl;
    anchor.download = `qr-stock-stc-${String(createdQr.item.id).padStart(3, "0")}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const printCreatedQr = () => {
    if (!createdQr) return;
    const popup = window.open("", "_blank", "width=620,height=760");
    if (!popup) return setMessage({ tone: "error", text: "Autorisez les fenêtres surgissantes pour imprimer le QR code." });
    const safe = (value: string) => value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#039;" })[character] ?? character);
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>QR ${safe(createdQr.item.brand)}</title><style>@page{size:A6 portrait;margin:8mm}body{margin:0;font-family:Arial,sans-serif;color:#0b1f33}.label{display:grid;justify-items:center;gap:8px;padding:14px;border:2px solid #0b1f33;text-align:center}.head{width:100%;padding-bottom:8px;border-bottom:1px solid #cad3da;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.08em}.brand{font-size:22px;font-weight:900}.model{font-size:14px}.location{padding:7px 10px;border-radius:7px;background:#edf2f5;font-weight:800}.reference{font-size:11px;color:#66717d}img{width:260px;height:260px}@media print{button{display:none}}</style></head><body><section class="label"><div class="head">STC · ${safe(createdQr.item.stock_area)}</div><img src="${createdQr.dataUrl}" alt="QR code"><div class="brand">${safe(createdQr.item.brand)}</div><div class="model">${safe(createdQr.item.model)}</div><div class="location">Emplacement ${safe(createdQr.item.location)}</div><div class="reference">Réf. ${String(createdQr.item.id).padStart(3, "0")} · Scanner pour sortie, retour ou réception</div></section><script>window.addEventListener('load',()=>window.print())</script></body></html>`);
    popup.document.close();
  };

  const adjustStock = async () => {
    if (!selected) return;
    const amount = Math.abs(Number(adjustment.quantity));
    if (!Number.isInteger(amount) || amount <= 0 || !adjustment.reason.trim()) return setMessage({ tone: "error", text: "Indiquez une quantité et un motif." });
    setBusy(true); setMessage(null);
    const { error } = await supabase.rpc("admin_adjust_stock", {
      p_item_id: selected.id,
      p_quantity_delta: adjustment.direction === "add" ? amount : -amount,
      p_reason: adjustment.reason.trim(),
    });
    setBusy(false);
    if (error) return setMessage({ tone: "error", text: error.message.includes("negative_stock") ? "Impossible : le stock deviendrait négatif." : error.message });
    setMessage({ tone: "success", text: adjustment.direction === "add" ? `${amount} pièce(s) ajoutée(s) au stock.` : `${amount} pièce(s) retirée(s) du stock.` });
    setAdjustment({ quantity: 1, direction: "add", reason: "Réception de matériel" }); await load(true);
  };

  const exportInventory = async () => {
    if (items.length === 0) return setMessage({ tone: "error", text: "Aucun article à exporter." });
    setExporting(true); setMessage(null);
    try {
      const historyWarnings: string[] = [];
      const safeHistoryLoad = async (label: string, table: "withdrawals" | "returns" | "stock_adjustments", selection: string) => {
        try {
          return await loadAllExportRows(table, selection);
        } catch (error) {
          console.warn(`Export STC : historique ${label} indisponible`, error);
          historyWarnings.push(label);
          return [];
        }
      };
      const [withdrawalRows, returnRows, adjustmentRows] = await Promise.all([
        safeHistoryLoad("sorties", "withdrawals", "*, items(id,brand,model,stock_area,location)"),
        safeHistoryLoad("retours", "returns", "*"),
        safeHistoryLoad("ajustements administrateur", "stock_adjustments", "*, items(id,brand,model,stock_area,location)"),
      ]);
      const withdrawals = withdrawalRows as unknown as ExportWithdrawal[];
      const returns = returnRows as unknown as ExportReturn[];
      const adjustments = adjustmentRows as unknown as ExportAdjustment[];
      const withdrawalById = new Map(withdrawals.map((entry) => [entry.id, entry]));
      const movements: ExportMovement[] = [
        ...withdrawals.map((entry): ExportMovement => ({
          createdAt: new Date(entry.created_at),
          movement: "Sortie",
          itemId: entry.item_id,
          brand: entry.items?.brand ?? "",
          model: entry.items?.model ?? "",
          stockArea: entry.items?.stock_area ?? "",
          location: entry.items?.location ?? "",
          personName: entry.person_name,
          personEmail: entry.person_email,
          quantity: -entry.quantity,
          usage: entry.usage_type === "temporary" ? "Momentané" : "Installation fixe",
          destination: entry.destination,
          expectedReturn: entry.expected_return_at ? new Date(`${entry.expected_return_at}T12:00:00`) : null,
          status: `${entry.status === "open" ? "Ouvert" : "Clôturé"}${entry.notes ? ` · ${entry.notes}` : ""}`,
        })),
        ...returns.map((entry): ExportMovement => {
          const withdrawal = withdrawalById.get(entry.withdrawal_id);
          return {
            createdAt: new Date(entry.created_at),
            movement: "Retour",
            itemId: withdrawal?.item_id ?? 0,
            brand: withdrawal?.items?.brand ?? "",
            model: withdrawal?.items?.model ?? "",
            stockArea: withdrawal?.items?.stock_area ?? "",
            location: withdrawal?.items?.location ?? "",
            personName: entry.person_name,
            personEmail: withdrawal?.person_email ?? "",
            quantity: entry.quantity,
            usage: withdrawal?.usage_type === "fixed" ? "Installation fixe" : "Momentané",
            destination: withdrawal?.destination ?? "",
            expectedReturn: withdrawal?.expected_return_at ? new Date(`${withdrawal.expected_return_at}T12:00:00`) : null,
            status: `Retour enregistré${entry.notes ? ` · ${entry.notes}` : ""}`,
          };
        }),
        ...adjustments.map((entry): ExportMovement => ({
          createdAt: new Date(entry.created_at),
          movement: entry.quantity_delta > 0 ? "Ajout administrateur" : "Retrait administrateur",
          itemId: entry.item_id,
          brand: entry.items?.brand ?? "",
          model: entry.items?.model ?? "",
          stockArea: entry.items?.stock_area ?? "",
          location: entry.items?.location ?? "",
          personName: entry.person_name,
          personEmail: "",
          quantity: entry.quantity_delta,
          usage: "Ajustement de stock",
          destination: entry.reason,
          expectedReturn: null,
          status: `Stock ${entry.quantity_before} → ${entry.quantity_after}`,
        })),
      ].sort((left, right) => right.createdAt.getTime() - left.createdAt.getTime());

      const excelModule = await import("exceljs") as typeof import("exceljs") & { default?: typeof import("exceljs") };
      const WorkbookConstructor = excelModule.Workbook ?? excelModule.default?.Workbook;
      if (!WorkbookConstructor) throw new Error("Le moteur Excel n’a pas pu être chargé par le navigateur.");
      const workbook = new WorkbookConstructor();
      workbook.creator = "Stock Conférence STC";
      workbook.created = new Date();
      workbook.modified = new Date();

      const sheet = workbook.addWorksheet("Inventaire du stock", {
        views: [{ state: "frozen", ySplit: 4 }],
        pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });
      sheet.mergeCells("A1:J1");
      sheet.getCell("A1").value = "Inventaire du stock — Stock Conférence STC";
      sheet.getCell("A1").font = { name: "Arial", size: 18, bold: true, color: { argb: "FFFFFFFF" } };
      sheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
      sheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0B1F33" } };
      sheet.getRow(1).height = 34;

      sheet.mergeCells("A2:J2");
      sheet.getCell("A2").value = `Export du ${new Intl.DateTimeFormat("fr-BE", { dateStyle: "long", timeStyle: "short" }).format(new Date())} · ${items.length} références`;
      sheet.getCell("A2").font = { name: "Arial", size: 10, color: { argb: "FF66717D" } };
      sheet.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };
      sheet.getRow(2).height = 23;

      sheet.addTable({
        name: "InventaireSTC",
        ref: "A4",
        headerRow: true,
        totalsRow: true,
        style: { theme: "TableStyleMedium2", showRowStripes: true },
        columns: [
          { name: "Référence", totalsRowLabel: "TOTAL" },
          { name: "Marque" },
          { name: "Modèle / désignation" },
          { name: "Bloc / stock" },
          { name: "Emplacement exact" },
          { name: "Quantité initiale", totalsRowFunction: "sum" },
          { name: "Stock actuel", totalsRowFunction: "sum" },
          { name: "Seuil d’alerte" },
          { name: "Quantité vérifiée" },
          { name: "Statut" },
        ],
        rows: items.map((item) => [
          String(item.id).padStart(3, "0"),
          item.brand,
          item.model,
          item.stock_area,
          item.location,
          item.initial_quantity,
          item.current_quantity,
          item.alert_threshold,
          item.quantity_verified ? "Oui" : "Non",
          item.active ? "Actif" : "Inactif",
        ]),
      });

      const widths = [12, 20, 38, 18, 20, 18, 16, 16, 19, 14];
      widths.forEach((width, index) => { sheet.getColumn(index + 1).width = width; });
      sheet.getColumn(1).alignment = { horizontal: "center" };
      [6, 7, 8].forEach((column) => {
        sheet.getColumn(column).numFmt = "#,##0";
        sheet.getColumn(column).alignment = { horizontal: "center" };
      });
      [5, 9, 10].forEach((column) => { sheet.getColumn(column).alignment = { horizontal: "center" }; });

      items.forEach((item, index) => {
        const row = sheet.getRow(5 + index);
        const stockCell = row.getCell(7);
        stockCell.font = { name: "Arial", bold: true };
        const color = !item.quantity_verified
          ? "FFE5E7EB"
          : item.current_quantity <= 0
            ? "FFFEE2E2"
            : item.current_quantity <= item.alert_threshold
              ? "FFFFF1D6"
              : "FFE6F5ED";
        stockCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
      });
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 4) row.alignment = { ...row.alignment, vertical: "middle" };
      });
      sheet.autoFilter = { from: "A4", to: `J${4 + items.length}` };
      sheet.headerFooter.oddFooter = "&LStock Conférence STC&CPage &P / &N&RExport administratif";

      const historySheet = workbook.addWorksheet("Historique des mouvements", {
        views: [{ state: "frozen", ySplit: 4 }],
        pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });
      historySheet.mergeCells("A1:N1");
      historySheet.getCell("A1").value = "Historique des mouvements — Stock Conférence STC";
      historySheet.getCell("A1").font = { name: "Arial", size: 18, bold: true, color: { argb: "FFFFFFFF" } };
      historySheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
      historySheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0B1F33" } };
      historySheet.getRow(1).height = 34;

      historySheet.mergeCells("A2:N2");
      historySheet.getCell("A2").value = `${movements.length} mouvement(s) · sorties, retours et ajustements administrateur · export du ${new Intl.DateTimeFormat("fr-BE", { dateStyle: "long", timeStyle: "short" }).format(new Date())}`;
      historySheet.getCell("A2").font = { name: "Arial", size: 10, color: { argb: "FF66717D" } };
      historySheet.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };
      historySheet.getRow(2).height = 23;

      if (historyWarnings.length > 0) {
        historySheet.mergeCells("A3:N3");
        historySheet.getCell("A3").value = `Historique partiel : données indisponibles pour ${historyWarnings.join(", ")}. L’inventaire du stock reste complet.`;
        historySheet.getCell("A3").font = { name: "Arial", size: 10, bold: true, color: { argb: "FF784309" } };
        historySheet.getCell("A3").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF1D8" } };
        historySheet.getCell("A3").alignment = { vertical: "middle", horizontal: "left" };
        historySheet.getRow(3).height = 23;
      }

      historySheet.addTable({
        name: "MouvementsSTC",
        ref: "A4",
        headerRow: true,
        totalsRow: true,
        style: { theme: "TableStyleMedium2", showRowStripes: true },
        columns: [
          { name: "Date et heure", totalsRowLabel: "TOTAL NET" },
          { name: "Mouvement" },
          { name: "Référence" },
          { name: "Marque" },
          { name: "Modèle / désignation" },
          { name: "Bloc / stock" },
          { name: "Emplacement exact" },
          { name: "Personne" },
          { name: "Adresse e-mail" },
          { name: "Quantité (+/−)", totalsRowFunction: "sum" },
          { name: "Usage" },
          { name: "Destination / motif" },
          { name: "Retour prévu" },
          { name: "Statut / note" },
        ],
        rows: movements.map((movement) => [
          movement.createdAt,
          movement.movement,
          movement.itemId ? String(movement.itemId).padStart(3, "0") : "",
          movement.brand,
          movement.model,
          movement.stockArea,
          movement.location,
          movement.personName,
          movement.personEmail,
          movement.quantity,
          movement.usage,
          movement.destination,
          movement.expectedReturn,
          movement.status,
        ]),
      });

      const historyWidths = [21, 24, 12, 18, 38, 18, 20, 24, 34, 16, 20, 32, 16, 36];
      historyWidths.forEach((width, index) => { historySheet.getColumn(index + 1).width = width; });
      historySheet.getColumn(1).numFmt = "dd/mm/yyyy hh:mm";
      historySheet.getColumn(13).numFmt = "dd/mm/yyyy";
      historySheet.getColumn(3).alignment = { horizontal: "center" };
      historySheet.getColumn(7).alignment = { horizontal: "center" };
      historySheet.getColumn(10).numFmt = "+0;-0;0";
      historySheet.getColumn(10).alignment = { horizontal: "center" };
      historySheet.getColumn(13).alignment = { horizontal: "center" };

      movements.forEach((movement, index) => {
        const row = historySheet.getRow(5 + index);
        const movementCell = row.getCell(2);
        const quantityCell = row.getCell(10);
        const fill = movement.movement === "Retour"
          ? "FFE6F5ED"
          : movement.movement === "Sortie"
            ? "FFFEE9E7"
            : movement.quantity > 0
              ? "FFE8F4FB"
              : "FFFFF1D8";
        movementCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
        movementCell.font = { name: "Arial", bold: true, color: { argb: "FF0B1F33" } };
        quantityCell.font = { name: "Arial", bold: true, color: { argb: movement.quantity >= 0 ? "FF177250" : "FFB42318" } };
      });
      historySheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 4) row.alignment = { ...row.alignment, vertical: "middle" };
      });
      historySheet.headerFooter.oddFooter = "&LStock Conférence STC&CPage &P / &N&RHistorique administratif";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      anchor.href = url;
      anchor.download = `stock-stc-inventaire-historique-${date}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setMessage({
        tone: "success",
        text: historyWarnings.length === 0
          ? "Le fichier Excel avec l’inventaire et l’historique a été téléchargé."
          : `Le fichier Excel a été téléchargé. L’inventaire est complet ; l’historique est partiel (${historyWarnings.join(", ")}).`,
      });
    } catch (error) {
      console.error("Export Excel STC impossible", error);
      const detail = error instanceof Error && error.message ? ` Détail : ${error.message}` : "";
      setMessage({ tone: "error", text: `L’export Excel n’a pas pu être créé.${detail}` });
    } finally {
      setExporting(false);
    }
  };

  return <>
    <PageHeading eyebrow="Administration" title="Gérer le stock" description="Ajoutez des références, corrigez les emplacements et mettez les quantités à jour." action={<div className="heading-actions"><button className="secondary-button" type="button" onClick={exportInventory} disabled={exporting || loading}>{exporting ? "Création du fichier…" : "Exporter Excel"}</button><button className="primary-button" type="button" onClick={startNew}>+ Ajouter un article</button></div>} />
    {message && <Notice tone={message.tone}>{message.text}</Notice>}
    {!loading && <InventoryImportPanel items={items} onImported={() => load(false)} />}
    {createdQr && <section className="created-qr-card no-print" aria-live="polite">
      <img src={createdQr.dataUrl} alt={`QR code de ${createdQr.item.brand} ${createdQr.item.model}`} />
      <div><p className="eyebrow">Nouvelle référence #{String(createdQr.item.id).padStart(3, "0")}</p><h2>{createdQr.item.brand} · {createdQr.item.model}</h2><p>{createdQr.item.stock_area} · Emplacement {createdQr.item.location}</p><small>Ce QR ouvre directement la fiche pour enregistrer une sortie, un retour ou une réception.</small></div>
      <div className="created-qr-actions"><button className="secondary-button" type="button" onClick={downloadCreatedQr}>Télécharger le QR</button><button className="primary-button" type="button" onClick={printCreatedQr}>Imprimer l’étiquette</button></div>
    </section>}
    <div className="inventory-admin-grid">
      <section className="inventory-picker table-card">
        <div className="picker-search"><label><span>Rechercher une référence</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Marque, modèle ou emplacement…" /></label></div>
        {loading ? <LoadingBlock /> : <div className="admin-item-list">{filtered.map((item) => <button type="button" className={selectedId === item.id ? "selected" : ""} key={item.id} onClick={() => selectItem(item)}><div><strong>{item.brand}</strong><span>{item.model}</span></div><div><span className="exact-location">{item.stock_area} · {item.location}</span><StockBadge item={item} /></div></button>)}{filtered.length === 0 && <div className="empty-state">Aucun article trouvé.</div>}</div>}
      </section>

      <section className="form-card inventory-editor">
        {selectedId === null ? <div className="empty-state large"><strong>Sélectionnez un article</strong><span>Vous pourrez modifier ses informations, son emplacement et sa quantité.</span></div> : <>
          <div className="section-title"><div><p className="eyebrow">{selectedId === "new" ? "Nouvelle référence" : `Référence #${selectedId}`}</p><h2>{selectedId === "new" ? "Ajouter un article" : `${selected?.brand} · ${selected?.model}`}</h2></div>{selected && <StockBadge item={selected} />}</div>
          <form className="checkout-form" onSubmit={save}>
            <div className="form-grid"><label><span>Marque *</span><input value={form.brand} onChange={(event) => setForm({ ...form, brand: event.target.value })} required /></label><label><span>Modèle / désignation *</span><input value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} required /></label></div>
            <div className="form-grid"><label><span>Bloc / stock *</span><input value={form.stockArea} onChange={(event) => setForm({ ...form, stockArea: event.target.value })} required /></label><label><span>Emplacement exact *</span><input value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value.toUpperCase() })} placeholder="Ex. F3" required /></label></div>
            <div className="form-grid"><label><span>{selectedId === "new" ? "Quantité de départ" : "Stock physique actuel"}</span><input type="number" min="0" step="1" value={form.quantity} onChange={(event) => setForm({ ...form, quantity: Number(event.target.value) })} /></label><label><span>Seuil d’alerte e-mail</span><input type="number" min="0" step="1" value={form.alertThreshold} onChange={(event) => setForm({ ...form, alertThreshold: Number(event.target.value) })} /><small className="field-help">Exemple : avec 4, l’alerte part lorsque le stock atteint 4 pièces.</small></label></div>
            <div className="checks-grid"><label className="check-row"><input type="checkbox" checked={form.quantityVerified} onChange={(event) => setForm({ ...form, quantityVerified: event.target.checked })} /><span>Quantité vérifiée</span></label><label className="check-row"><input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} /><span>Article actif et visible</span></label></div>
            <button className="primary-button full-width" disabled={busy}>{busy ? "Enregistrement…" : selectedId === "new" ? "Ajouter à la base" : "Enregistrer les modifications"}</button>
          </form>

          {selected && <div className="stock-adjustment"><div><p className="eyebrow">Mouvement administrateur</p><h3>Ajouter ou retirer du stock</h3><p>Chaque correction est enregistrée avec votre nom et son motif.</p></div><div className="adjustment-grid"><label><span>Opération</span><select value={adjustment.direction} onChange={(event) => setAdjustment({ ...adjustment, direction: event.target.value as "add" | "remove", reason: event.target.value === "add" ? "Réception de matériel" : "Correction d’inventaire" })}><option value="add">Ajouter</option><option value="remove">Retirer</option></select></label><label><span>Quantité</span><input type="number" min="1" value={adjustment.quantity} onChange={(event) => setAdjustment({ ...adjustment, quantity: Number(event.target.value) })} /></label><label className="reason-field"><span>Motif</span><input value={adjustment.reason} onChange={(event) => setAdjustment({ ...adjustment, reason: event.target.value })} /></label><button className="secondary-button" type="button" onClick={adjustStock} disabled={busy}>Mettre le stock à jour</button></div></div>}
        </>}
      </section>
    </div>
  </>;
}
